// src/context/SeasonalThemeContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import api from '../api/axiosConfig';
import hexToRgba from '../utils/hexToRgba';

// The currently-active saved holiday/seasonal theme (if any) — fetched once
// on mount with no login required (it can override the login page's own
// logo, so it has to work pre-auth too, unlike IconSettingsContext/
// SectionColorContext which only matter once you're in the navbar).
// Navbar.js/Login.js read `activeTheme.*_logo_light/dark` directly for their
// <img> swaps; the navbar background color is instead applied here as a
// small global <style> override (same technique SectionColorContext uses
// for hover colors) since --sb-outer-bg already drives every navbar surface.
//
// `previewTheme` (set from Settings' "Probar" button) overrides `activeTheme`
// for this browser tab/session only — it's never sent to the server, so it
// never affects the real schedule or any other user. It's kept in
// sessionStorage (not localStorage) so a preview never accidentally survives
// into a later day/session and gets mistaken for a real active theme.
const SeasonalThemeContext = createContext({
  activeTheme: null, realTheme: null, previewTheme: null, isPreview: false,
  setPreviewTheme: () => {}, refresh: () => {},
});

export const useSeasonalTheme = () => useContext(SeasonalThemeContext);

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const PREVIEW_KEY = 'pf-theme-preview';

// A single color, or (when the second stop is also set) a gradient between
// the two — either way it's just one value for --sb-outer-bg to hold, since
// `background: var(--sb-outer-bg)` accepts a plain color or a full
// background-image value (like linear-gradient(...)) equally well.
function bgValue(color1, color2) {
  if (!HEX_COLOR_RE.test(color1)) return null;
  if (HEX_COLOR_RE.test(color2)) return `linear-gradient(135deg, ${color1}, ${color2})`;
  return color1;
}

function navbarBgCss(theme) {
  if (!theme) return '';
  const rules = [];

  const darkBg = bgValue(theme.navbar_bg_dark, theme.navbar_bg_dark_2);
  if (darkBg) rules.push(`:root { --sb-outer-bg: ${darkBg} !important; }`);
  const lightBg = bgValue(theme.navbar_bg_light, theme.navbar_bg_light_2);
  if (lightBg) rules.push(`[data-theme="light"] { --sb-outer-bg: ${lightBg} !important; }`);

  // --sb-glow is consumed as a box-shadow color, so it needs an actual alpha
  // channel — a plain hex would render as a hard-edged solid ring instead of
  // a soft glow, so this always converts through hexToRgba with a fixed alpha.
  if (HEX_COLOR_RE.test(theme.navbar_glow_dark)) {
    rules.push(`:root { --sb-glow: ${hexToRgba(theme.navbar_glow_dark, 0.35)} !important; }`);
  }
  if (HEX_COLOR_RE.test(theme.navbar_glow_light)) {
    rules.push(`[data-theme="light"] { --sb-glow: ${hexToRgba(theme.navbar_glow_light, 0.45)} !important; }`);
  }

  // --sb-border is likewise a border color that needs its own alpha — a
  // fully opaque hex would look like a much heavier, flatter edge than the
  // subtle translucent line the navbar normally has.
  if (HEX_COLOR_RE.test(theme.navbar_border_dark)) {
    rules.push(`:root { --sb-border: ${hexToRgba(theme.navbar_border_dark, 0.4)} !important; }`);
  }
  if (HEX_COLOR_RE.test(theme.navbar_border_light)) {
    rules.push(`[data-theme="light"] { --sb-border: ${hexToRgba(theme.navbar_border_light, 0.6)} !important; }`);
  }

  // --sb-bubble-pulse — the pulsing glow ring around "bubble mode"'s button.
  if (HEX_COLOR_RE.test(theme.navbar_pulse_dark)) {
    rules.push(`:root { --sb-bubble-pulse: ${hexToRgba(theme.navbar_pulse_dark, 0.35)} !important; }`);
  }
  if (HEX_COLOR_RE.test(theme.navbar_pulse_light)) {
    rules.push(`[data-theme="light"] { --sb-bubble-pulse: ${hexToRgba(theme.navbar_pulse_light, 0.55)} !important; }`);
  }

  return rules.join('\n');
}

function readStoredPreview() {
  try {
    return JSON.parse(sessionStorage.getItem(PREVIEW_KEY) || 'null');
  } catch {
    return null;
  }
}

export const SeasonalThemeProvider = ({ children }) => {
  const [realTheme, setRealTheme] = useState(null);
  const [previewTheme, setPreviewThemeState] = useState(readStoredPreview);

  const refresh = useCallback(() => {
    api.get('settings/themes/active/')
      .then(r => setRealTheme(r.data || null))
      .catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setPreviewTheme = useCallback((theme) => {
    setPreviewThemeState(theme);
    try {
      if (theme) sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(theme));
      else sessionStorage.removeItem(PREVIEW_KEY);
    } catch { /* ignore */ }
  }, []);

  const activeTheme = previewTheme || realTheme;
  const isPreview = !!previewTheme;
  const style = useMemo(() => navbarBgCss(activeTheme), [activeTheme]);

  return (
    <SeasonalThemeContext.Provider value={{ activeTheme, realTheme, previewTheme, isPreview, setPreviewTheme, refresh }}>
      <style>{style}</style>
      {isPreview && (
        <div style={{
          position: 'fixed', bottom: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 99,
          background: '#111827', color: '#fff', fontSize: 12.5, fontWeight: 600,
          boxShadow: '0 6px 18px rgba(0,0,0,.35)',
        }}>
          👁️ Vista previa: {previewTheme.name}
          <button
            type="button"
            onClick={() => setPreviewTheme(null)}
            style={{
              background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: 99,
              color: '#fff', fontSize: 11.5, fontWeight: 700, padding: '4px 10px', cursor: 'pointer',
            }}
          >
            Salir
          </button>
        </div>
      )}
      {children}
    </SeasonalThemeContext.Provider>
  );
};
